import React, { useState, useEffect, useMemo } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import { MdOutlineFileUpload } from "react-icons/md";
import { Container, Spinner } from "react-bootstrap";
import { baseUrl } from "../../../../network/constants/constants";
import { getConfig } from "../../../../network/auth";
import axios from "../../../../network/api";
import Snackbar from "@mui/material/Snackbar";
import "./bannerScreen.css";

const BannerImage = () => {
  const [loading, setLoading] = useState(false);
  const [bannerList, setBannerList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteImageId, setDeleteImageId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleOpenDelete = (imageId) => {
    setDeleteImageId(imageId);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteImageId) {
      deleteImage(deleteImageId);
    }
    handleCloseDelete();
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleCloseDelete = () => setDeleteOpen(false);

  const config = useMemo(() => {
    return getConfig();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: "Please select a banner image!",
        severity: "error",
      });
      return;
    }

    const formData = new FormData();
    formData.append("image", selectedFile);
    formData.append("link_type", "ext_link");

    setLoading(true);

    axios
      .post(baseUrl + `app/banner/create/`, formData, {
        headers: {
          Authorization: config.headers.Authorization,
        },
      })
      .then((response) => {
        setSnackbar({
          open: true,
          message: "Banner uploaded successfully!",
          severity: "success",
        });
        fetchBanner();
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: "Failed to upload banner.",
          severity: "error",
        });
      })
      .finally(() => {
        setLoading(false);
        setSelectedFile(null);
        setPreviewUrl(null);
      });
  };

  const fetchBanner = () => {
    setLoading(true);
    axios
      .get(baseUrl + `app/banner/list/`, config)
      .then((response) => {
        setBannerList(response.data.results);
      })
      .catch((error) => {
        console.error("Gallery Error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteImage = (imageId) => {
    setLoading(true);
    axios
      .delete(baseUrl + `app/banner/delete/${imageId}/`, config)
      .then(() => {
        setSnackbar({
          open: true,
          message: "Banner deleted successfully",
          severity: "success",
        });
        fetchBanner();
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: "Failed to delete banner.",
          severity: "error",
        });
        console.error("Delete Error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchBanner();
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, []);

  return (
    <Container fluid className="mb-3">
      <div className="banner-img">
        {loading ? (
          <div className="loader-container">
            <Spinner className="spinner-loader" />
          </div>
        ) : (
          <ResponsiveMasonry
            columnsCountBreakPoints={{ 400: 1, 800: 2, 900: 4 }}
          >
            <Masonry gutter="20px">
              {bannerList.length === 0 ? (
                <div className="no-images-message">No images are available</div>
              ) : (
                bannerList.map((image) => (
                  <div className="banner-img-div" key={image.id}>
                    <span
                      className="material-icons delete-icon"
                      onClick={() => handleOpenDelete(image.id)}
                    >
                      delete_forever
                    </span>

                    <Dialog
                      open={deleteOpen}
                      onClose={handleCloseDelete}
                      PaperProps={{
                        style: { borderRadius: "20px" },
                      }}
                    >
                      <DialogContent>
                        <div className="delete-img-div">
                          <h3 className="delete-img-heading">Are you sure?</h3>
                          <p className="delete-img-p">
                            Do you really want to delete?
                          </p>
                          <div className="delete-btn-div">
                            <button onClick={handleConfirmDelete}>
                              Delete
                            </button>
                            <button onClick={handleCloseDelete}>Cancel</button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <img
                      src={`${image.image}`}
                      style={{
                        width: "100%",
                        display: "block",
                        cursor: "pointer",
                      }}
                      alt="Service"
                    />
                  </div>
                ))
              )}
            </Masonry>
          </ResponsiveMasonry>
        )}
        <button className="btn-add-banner-image " onClick={handleOpen}>
          + Add Banner Image
        </button>
        <Dialog
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: { borderRadius: "20px" },
          }}
        >
          <DialogTitle className="add-img-heading">
            Add Banner Images
          </DialogTitle>
          <DialogContent>
            <div className="add-img-div">
              <form
                method="post"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleUpload();
                  handleClose();
                }}
              >
                <label className="add-file-upload mb-1">
                  <input
                    className="add-img-file"
                    id="file"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <MdOutlineFileUpload className="upload-icon" />
                </label>

                {previewUrl && (
                  <div className="image-preview-container">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="preview-image"
                    />
                  </div>
                )}

                <DialogActions>
                  <Button type="submit" variant="contained" color="primary">
                    Upload
                  </Button>
                  <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="secondary"
                  >
                    Cancel
                  </Button>
                </DialogActions>
              </form>
            </div>
          </DialogContent>
        </Dialog>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </Container>
  );
};
export default BannerImage;
